import {
	getPopupList,
	getNoticeBoard,
	getBannerList,
	getPopularGGoodsList,
	getThemeList,
	getThemeGGoodsList,
} from '@/api/main/main';

import _sortBy from 'lodash/sortBy';
import { crmMainBizPopularTabClick, crmMainBizThemeTabClick } from '@/helpers/ad/crm/crm.js';

export const home = {
	namespaced: true,

	state: {
		notice: undefined,
		banners: [],

		selectedPopularCategory: undefined,
		popularProducts: {},

		selectedThemeSeq: undefined,
		themes: [],
		themeProducts: {},

		mainPopups: {},
	},

	mutations: {
		SET_NOTICE(state, payload) {
			state.notice = payload;
		},

		SET_BANNERS(state, payload) {
			if (!payload) return;
			state.banners = payload;
		},

		SET_SELECTED_POPULAR_CATEGORY(state, categorySeq) {
			state.selectedPopularCategory = categorySeq;
		},

		SET_POPULAR_PRODUCTS(state, { key, list }) {
			if (!list) return;
			state.popularProducts = {
				...state.popularProducts,
				[key]: list,
			};
		},

		SET_SELECTED_THEME_SEQ(state, themeSeq) {
			state.selectedThemeSeq = themeSeq;
		},

		SET_THEMES(state, themes) {
			state.themes = themes;
		},

		SET_THEME_PRODUCTS(state, { key, list }) {
			if (!list) return;
			state.themeProducts = {
				...state.themeProducts,
				[key]: list,
			};
		},

		SET_MAIN_POPUPS(state, popups = {}) {
			state.mainPopups = popups;
		},
	},

	actions: {
		/** 메인 공지사항 호출 */
		// gs.song 2023.07.24 channelDispyCd 파라미터 추가
		async fetchNotice({ commit }, payload) {
			try {
				const { code, message, result } = await getNoticeBoard(payload);
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);
				commit('SET_NOTICE', result);
			} catch (error) {
				console.error(error);
			}
		},

		/** 배너 목록 호출 */
		async fetchBanners({ commit }) {
			try {
				const { code, message, result } = await getBannerList({ accessChannel: 'MOBILE' });
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);
				commit('SET_BANNERS', result);
			} catch (error) {
				console.error(error);
			}
		},

		/** 인기상품 탭 선택 */
		selectPopularCategory({ commit, dispatch }, { categorySeq, categoryName }) {
			commit('SET_SELECTED_POPULAR_CATEGORY', categorySeq);
			dispatch('fetchPopularProducts', categorySeq);

			/** crm biz 인기상품 카테고리 탭 클릭 */
			crmMainBizPopularTabClick(categoryName);
		},

		/** 인기상품 목록 호출 */
		async fetchPopularProducts({ commit }, categorySeqList) {
			try {
				const { code, message, result } = await getPopularGGoodsList({ categorySeq: categorySeqList });
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);

				// 인기상품이 카테고리 단일 구조이므로, array로 형식 통일
				result.forEach(item => {
					item.categoryList = [item.category1Seq];
				});

				commit('SET_POPULAR_PRODUCTS', {
					key: categorySeqList?.toString() ?? 'all',
					list: result,
				});
			} catch (error) {
				console.error(error);
			}
		},

		/** 테마 선택 */
		selectTheme({ commit, dispatch }, { themeSeq, themaName }) {
			commit('SET_SELECTED_THEME_SEQ', themeSeq);
			dispatch('fetchThemeProducts', themeSeq);

			/** crm biz 인기상품 카테고리 탭 클릭 */
			if (themaName) crmMainBizThemeTabClick(themaName);
		},

		/** 테마 목록 호출 */
		async fetchThemes({ commit }) {
			try {
				const { code, message, result } = await getThemeList({});
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);

				commit('SET_THEMES', result);
			} catch (error) {
				console.error(error);
			}
		},

		/** 테마 상품 목록 호출 */
		async fetchThemeProducts({ commit }, themeSeq) {
			if (!themeSeq) return;
			try {
				const { code, message, result } = await getThemeGGoodsList({ exhThemaSeq: themeSeq });
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);

				commit('SET_THEME_PRODUCTS', {
					key: themeSeq ?? 'all',
					list: result,
				});
			} catch (error) {
				console.error(error);
			}
		},

		/** 메인 팝업 목록 호출 */
		async fetchMainPopups({ commit }) {
			try {
				const { code, message, result } = await getPopupList({});
				if (code !== 'SUC0000') throw new Error(`${code}: ${message}`);

				commit('SET_MAIN_POPUPS', result);
			} catch (error) {
				console.error(error);
			}
		},

		/** 탭, 카테고리 등 사용자 선택 정보를 초기화 */
		clearSelectedInfo({ dispatch }) {
			dispatch('selectTheme', { themeSeq: null, themaName: null });
			dispatch('selectPopularCategory', { categorySeq: null, categoryName: null });
			dispatch('fetchThemes');
			dispatch('fetchThemeProducts');
		},
	},

	getters: {
		notice: state => state.notice,
		topBanners: state =>
			_sortBy(
				state.banners.filter(({ bannerPosCd }) => bannerPosCd === 'TOP'),
				'sort'
			),
		mainBanners: state =>
			_sortBy(
				state.banners.filter(({ bannerPosCd }) => bannerPosCd === 'MAIN'),
				'sort'
			).slice(0, 10),
		midBanners: state =>
			_sortBy(
				state.banners.filter(({ bannerPosCd }) => bannerPosCd === 'MIDDLE'),
				'sort'
			),
		gnbBanners: state =>
			_sortBy(
				state.banners.filter(({ bannerPosCd }) => bannerPosCd === 'GNB'),
				'sort'
			),

		popularProductsAll: state => state.popularProducts,
		selectedPopularCategory: state => state.selectedPopularCategory,
		popularProducts: state => state.popularProducts[state.selectedPopularCategory ?? 'all'] ?? [],

		themes: state => state.themes,
		themeProducts: state => state.themeProducts,
		selectedThemeSeq: state => state.selectedThemeSeq,
		selectedThemeProducts: state => state.themeProducts[state.selectedThemeSeq ?? 'all'],

		noticePopups: state => state.mainPopups?.noticeList?.slice(0, 3) ?? [],
		popups: state => state.mainPopups?.mobilePopupList ?? [],
	},
};

export default home;
